import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';
import {
  CONFIGURABLE_EVENTS,
  SECURITY_EVENTS,
  defaultNotificationPrefs,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
} from '@wordpuppi/shared';
import type { ConfigurableEventKey } from '@wordpuppi/shared';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export const Route = createFileRoute('/account/notifications')({
  component: NotificationsPage,
});

// AB#572: desktop counterpart of the mobile prefs screen. Same shared client
// (@wordpuppi/shared notification-prefs, AB#571) → GET/PUT /notification-prefs
// (AB#570). Layout/labels/defaults come from the shared metadata so the two
// screens stay in lockstep; GET stays authoritative for values.
const GROUP_LABELS: Record<string, string> = {
  deploy: 'Deploys',
  distribution: 'Distribution',
  account: 'Account',
  billing: 'Billing',
};
const GROUP_ORDER = ['deploy', 'distribution', 'account', 'billing'] as const;

function NotificationsPage() {
  const { data, isLoading, error } = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();

  // GET returns all 10 events; fall back to the shared default matrix until it
  // resolves so switches never flash the wrong default.
  const prefs = data?.data?.prefs ?? defaultNotificationPrefs();

  const setChannel = (
    key: ConfigurableEventKey,
    channel: 'email' | 'push',
    value: boolean,
  ) => {
    const current = prefs[key] ?? { email: false, push: false };
    // Optimistic + rollback handled inside the shared hook.
    update.mutate(
      { [key]: { ...current, [channel]: value } },
      {
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : 'Could not save your preference',
          ),
      },
    );
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Notifications</h1>
        <p className="text-sm text-muted-foreground">
          Choose which events reach you by email and push.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Notification preferences</CardTitle>
          <CardDescription>
            Toggles save automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && !data ? (
            <p className="text-sm text-destructive">
              Couldn't load your notification settings. Refresh the page to try again.
            </p>
          ) : isLoading && !data ? (
            <p className="text-sm text-muted-foreground">Loading your preferences…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-full">Event</TableHead>
                  <TableHead className="text-center w-24">Email</TableHead>
                  <TableHead className="text-center w-24">Push</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {GROUP_ORDER.map((group) => {
                  const events = CONFIGURABLE_EVENTS.filter((e) => e.group === group);
                  if (events.length === 0) return null;
                  return (
                    <Fragment key={group}>
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={3}
                          className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {GROUP_LABELS[group] ?? group}
                        </TableCell>
                      </TableRow>
                      {events.map((e) => {
                        const channels = prefs[e.key] ?? e.default;
                        return (
                          <TableRow key={e.key}>
                            <TableCell>
                              <div className="font-medium">{e.label}</div>
                              <div className="text-sm text-muted-foreground">
                                {e.description}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={channels.email}
                                onCheckedChange={(v) => setChannel(e.key, 'email', v)}
                                aria-label={`${e.label} email`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={channels.push}
                                onCheckedChange={(v) => setChannel(e.key, 'push', v)}
                                aria-label={`${e.label} push`}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  );
                })}

                {/* Security events: email locked ON, no push cell. The server
                    ignores client values for these (AB#570), so they render
                    non-interactive. */}
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={3}
                    className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Security
                  </TableCell>
                </TableRow>
                {SECURITY_EVENTS.map((e) => (
                  <TableRow key={e.key}>
                    <TableCell>
                      <div className="font-medium">{e.label}</div>
                      <div className="text-sm text-muted-foreground">{e.description}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch checked disabled aria-label={`${e.label} email, always on`} />
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            Security alerts are always sent by email and can't be turned off.
          </p>
        </CardContent>
      </Card>
    </>
  );
}
